import { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router';
import '../i18n';
import { AuthRoutes } from './Auth/routes/AuthRoutes';
import { PrivateRoute } from './Routes/PrivateRoute';
import { envConfig } from '@/config/envConfig';
import { USER_ROLE_MAP } from '@/const/user';
import { ProtectedRoute } from './Routes/ProtectedRoute';
import { SHARED_ORGANIZATION_ROUTES, PRIVATE_ROUTES } from './Routes/privateRoutes';
import { UserProvider } from '@/context/userContext';
import { AccessDeniedPage } from './pages/AccessDeniedPage/AccessDeniedPage';
import { getAccessDeniedPath } from '@/bundle/pages/AccessDeniedPage/path/path';
import { useOnline } from '@/hooks/useOnline';
import { removeSnackbarById, showSnackbar } from '@/components/Snackbar/Snackbar';
import { getSwitchRolePath } from '@/bundle/pages/SwitchRolePage/path/path';
import { SwitchRolePage } from '@/bundle/pages/SwitchRolePage/SwitchRolePage';
import { ProfilePage } from '@/bundle/pages/ProfilePage/ProfilePage';
import { getProfilePath } from '@/bundle/pages/ProfilePage/path/path';
import { ProfileProtectedRoute } from '@/bundle/pages/ProfilePage/ProfileProtectedRoute';
import { NotFoundPage } from './pages/NotFoundPage/NotFoundPage';
import { getBuyOverageWiresPath } from '@/bundle/_MasterAdmin/_BuyOverageWiresPage/path/path';
import { BuyOverageWiresPage } from '@/bundle/_MasterAdmin/_BuyOverageWiresPage/BuyOverageWiresPage';
import { getSinglePaymentCheckoutPath } from '@/bundle/_MasterAdmin/_SinglePaymentCheckoutPage/path/path';
import { SinglePaymentCheckoutPage } from '@/bundle/_MasterAdmin/_SinglePaymentCheckoutPage/SinglePaymentCheckoutPage ';
import { SubscriptionCheckoutPage } from './Auth/AdminOnboardingFlow/SubscriptionCheckoutPage/SubscriptionCheckoutPage';
import { getSubscriptionCheckoutPath } from './Auth/AdminOnboardingFlow/SubscriptionCheckoutPage/path';
import { getSelectBillingPlanPath } from './Auth/AdminOnboardingFlow/SelectBillingPlanPage/path';
import { SelectBillingPlanPage } from './Auth/AdminOnboardingFlow/SelectBillingPlanPage/SelectBillingPlanPage';
import { SubscriptionPurchasedProtectedRoute } from '@/bundle/Routes/SubscriptionPurchasedProtectedRoute';
import { SubscriptionRequiredProtectedRoute } from '@/bundle/Routes/SubscriptionRequiredProtectedRoute';
import { NotFoundAuthPage } from './pages/NotFoundPage/NotFoundAuthPage';
import { useCookiesPreferences } from '@/bundle/CookiesBanner/cookiesContext';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { getMasterAdminMasterServiceAgreementPath } from '@/bundle/_MasterAdmin/_MasterServiceAgreementPage/path/path';
import { MasterServiceAgreementPage } from '@/bundle/_MasterAdmin/_MasterServiceAgreementPage/MasterServiceAgreementPage';
import { getOpcoOrganizationMasterServiceAgreementPagePath } from '@/bundle/_Opco/ui/OrganizationMasterServiceAgreementPage/path';
import { OrganizationMasterServiceAgreementPage } from '@/bundle/_Opco/ui/OrganizationMasterServiceAgreementPage/OrganizationMasterServiceAgreementPage';
import { getAcceptMasterServiceAgreementPath } from './Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/path';
import { AcceptMasterServiceAgreementProtectedRoute } from './Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/AcceptMasterServiceAgreementProtectedRoute';
import { AcceptMasterServiceAgreementPage } from './Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/AcceptMasterServiceAgreementPage';
import { OnboardingGuideRoute } from './Routes/OnboardingGuideRoute';
import { SecureLinkPage } from '@/bundle/pages/SecureLinkPage/SecureLinkPage';
import { getSecureLinkPath } from '@/bundle/pages/SecureLinkPage/path/path';
import { RecipientRequestPage } from '@/bundle/pages/RecipientRequestPage/RecipientRequestPage';
import { getRecipientRequestPath } from '@/bundle/pages/RecipientRequestPage/path/path';
import { InvoiceLinkPage } from '@/bundle/pages/InvoiceLinkPage/InvoiceLinkPage';
import { getInvoiceLinkPath } from '@/bundle/pages/InvoiceLinkPage/path/path';
import { InvoiceCheckPage } from '@/bundle/pages/InvoiceCheckPage/InvoiceCheckPage';
import { getInvoiceCheckPath } from '@/bundle/pages/InvoiceCheckPage/path/path';

export const App = () => {
  const onlineRef = useRef(null);
  const isOnline = useOnline();
  const { hasPerformanceCookiesConsent } = useCookiesPreferences();

  const logBuildVersions = () => {
    // eslint-disable-next-line no-console
    console.log(
      `============< [Release version: ${envConfig.RELEASE_VERSION}], [Build version: ${envConfig.BUILD_VERSION}] >============`
    );
  };

  useEffect(() => {
    logBuildVersions();
  }, []);

  useEffect(() => {
    if (!isOnline) {
      onlineRef.current = showSnackbar('Application is currently offline.', { type: 'error', autoClose: false });

      return;
    }

    if (onlineRef.current) {
      removeSnackbarById(onlineRef.current);
    }
  }, [isOnline]);

  useEffect(() => {
    if (hasPerformanceCookiesConsent && !amplitudeService.initialized) {
      amplitudeService.init(hasPerformanceCookiesConsent);
    }

    if (hasPerformanceCookiesConsent) {
      amplitudeService.enableTracking();
    } else {
      amplitudeService.disableTracking();
    }
  }, [hasPerformanceCookiesConsent]);

  return (
    <Routes>
      {AuthRoutes}
      {/* Authentication private routes */}
      <Route element={<UserProvider />}>
        {/* Sidebar app routes */}
        <Route element={<OnboardingGuideRoute />}>
          <Route element={<PrivateRoute />}>
            <Route element={<ProfileProtectedRoute />}>
              <Route path={getProfilePath()} element={<ProfilePage />} />
            </Route>

            {/* Shared organization admin routes */}
            <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.MASTER_ADMIN, USER_ROLE_MAP.ORG_ADMIN]} />}>
              {SHARED_ORGANIZATION_ROUTES}
            </Route>

            {/* User role routes */}
            <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.OPCO]} />}>
              {PRIVATE_ROUTES[USER_ROLE_MAP.OPCO]}
            </Route>
            <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.MASTER_ADMIN]} />}>
              {PRIVATE_ROUTES[USER_ROLE_MAP.MASTER_ADMIN]}
            </Route>
            <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.DEPOSITOR]} />}>
              {PRIVATE_ROUTES[USER_ROLE_MAP.DEPOSITOR]}
            </Route>
            <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.EXECUTOR]} />}>
              {PRIVATE_ROUTES[USER_ROLE_MAP.EXECUTOR]}
            </Route>
            <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.RECIPIENT]} />}>
              {PRIVATE_ROUTES[USER_ROLE_MAP.RECIPIENT]}
            </Route>
          </Route>
        </Route>

        {/* Without sidebar app routes */}
        <Route element={<PrivateRoute renderLayout={false} />}>
          {/* Shared routes */}
          <Route path={getSwitchRolePath()} element={<SwitchRolePage />} />

          {/* [OpCo Admin] routes */}
          <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.OPCO]} />}>
            <Route
              key={getOpcoOrganizationMasterServiceAgreementPagePath()}
              path={getOpcoOrganizationMasterServiceAgreementPagePath()}
              element={<OrganizationMasterServiceAgreementPage />}
            />
          </Route>

          {/* [Master Admin] routes */}
          <Route element={<ProtectedRoute allowedRole={[USER_ROLE_MAP.MASTER_ADMIN]} />}>
            {/* Master Service Agreement routes */}
            <Route key={getAcceptMasterServiceAgreementPath()} element={<AcceptMasterServiceAgreementProtectedRoute />}>
              <Route
                key={getAcceptMasterServiceAgreementPath()}
                path={getAcceptMasterServiceAgreementPath()}
                element={<AcceptMasterServiceAgreementPage />}
              />
            </Route>
            <Route path={getMasterAdminMasterServiceAgreementPath()} element={<MasterServiceAgreementPage />} />
            {/* End of Master Service Agreement routes */}

            {/* Billing checkout related routes */}
            <Route element={<SubscriptionRequiredProtectedRoute />}>
              <Route path={getBuyOverageWiresPath()} element={<BuyOverageWiresPage />} />
              <Route path={getSinglePaymentCheckoutPath()} element={<SinglePaymentCheckoutPage />} />
            </Route>
            <Route element={<SubscriptionPurchasedProtectedRoute />}>
              <Route path={getSelectBillingPlanPath()} element={<SelectBillingPlanPage />} />
              <Route path={getSubscriptionCheckoutPath()} element={<SubscriptionCheckoutPage />} />
            </Route>
            {/* End of Billing checkout related routes */}
          </Route>
        </Route>

        <Route path={getAccessDeniedPath()} element={<AccessDeniedPage />} />

        <Route path='*' element={<NotFoundAuthPage />} />
      </Route>
      {/* Public routes without any layout */}
      <Route path={getSecureLinkPath()} element={<SecureLinkPage />} />
      <Route path={getRecipientRequestPath()} element={<RecipientRequestPage />} />
      <Route path={getInvoiceLinkPath()} element={<InvoiceLinkPage />} />
      <Route path={getInvoiceCheckPath()} element={<InvoiceCheckPage />} />
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  );
};
